import logger from "../../services/Logger";
import { FileStore } from "../../store/fileUploadStore";
import UserAuthStore from "../../store/userStore";
import { mapValue } from "../helpers";
import api from "../../api";
import ENDPOINTS from "../../api/endpoints";
import useSessionIdStore from "../../store/useSessionIdStore";
import mediaUrlAdapter from "../../api/Media/mediaUrlAdapter";
import { v4 as uuidv4 } from "uuid";

export const uploadFile = async (
  chat_id,
  fileInputRef,
  file__path,
  space,
  takeThisFile,
  setFilesOverride,
  uploadId
) => {
  const { user, orgId } = UserAuthStore.getState();
  const { incrementUploading, decrementUploading, setProgress, setFiles: globalSetFiles } = FileStore.getState();
  const isOverride = typeof setFilesOverride === 'function';
  const setFiles = isOverride ? setFilesOverride : globalSetFiles;

  incrementUploading();

  try {
    const file = takeThisFile ? takeThisFile : fileInputRef?.current?.files?.[0];
    if (!file) {
      throw new Error("No file provided for upload.");
    }
    const resolvedUploadId = uploadId || uuidv4();

    const abortController = new AbortController();
    const nFile = {
      name: file.name,
      size: file.size,
      downloadURL: "",
      file__path: file__path,
      progress: 0,
      abortController,
      upload_id: resolvedUploadId,
    };
    if (isOverride) {
      setFiles(prev => [...(prev || []), nFile]);
    } else {
      const currentFiles = FileStore.getState().files || [];
      setFiles([...currentFiles, nFile]);
    }

    const formData = new FormData();
    formData.append("file", file);
    if (chat_id) formData.append("chat_id", chat_id);
    formData.append("upload_id", resolvedUploadId);
    if (space?.space_id) {
      formData.append("space_id", space.space_id);
    }

    const importResponse = await api({
      method: "POST",
      url: "/storage/assets/upload",
      data: formData,
      signal: abortController.signal,
      onUploadProgress: (e) => {
        const currentProgress = e.total ? Math.round((e.loaded / e.total) * 100) : 0;
        const updateProgress = (arr) => (arr || []).map((f) =>
          f?.file__path === file__path
            ? { ...f, progress: mapValue(currentProgress, 0, 100, 0, 90) }
            : f
        );
        if (isOverride) {
          setFiles(prev => updateProgress(prev));
        } else {
          setFiles(updateProgress(FileStore.getState().files));
        }
      },
    });

    const asset = importResponse?.data?.asset;
    if (!asset?.asset_id) throw new Error("Asset upload returned no asset_id");
    const ownerId = space?.owner_id ? space.owner_id : user?.uid;
    const session_id = useSessionIdStore.getState().session_id;

    await api({
      method: "POST",
      url: ENDPOINTS.ADD_FILE_TO_SPARK,
      data: {
        chat_id,
        org_id: orgId,
        owner_id: ownerId,
        asset_id: asset.asset_id,
        file_toc: importResponse?.data?.file_toc ?? [],
        space_id: space?.space_id ?? null,
        by: user?.uid,
        session_id,
      },
    });

    const resolvedAsset = await mediaUrlAdapter.resolveAsset(asset.asset_id);

    const updateComplete = (arr) => (arr || []).map((f) =>
      f?.file__path === file__path
        ? {
            ...f,
            progress: 100,
            downloadURL: resolvedAsset.url,
            asset_id: asset.asset_id,
            mime_type: asset.mime_type,
          }
        : f
    );
    if (isOverride) {
      setFiles(prev => updateComplete(prev));
    } else {
      setFiles(updateComplete(FileStore.getState().files));
    }

    decrementUploading();
  } catch (error) {
    if (error.name === 'CanceledError' || error.name === 'AbortError') {
      decrementUploading();
      return;
    }
    logger.error('fileUpload', 'Error initiating upload', { error: error.message, chat_id });
    decrementUploading();
    setProgress(0);
    throw error;
  }
};

const uploadMultipleFiles = async (
  new_chat_files,
  user,
  orgId,
  chat_id,
  space
) => {
  const uploadPromises = Array.from(new_chat_files).map((queuedFile) => {
    const file = queuedFile?.file || queuedFile;
    const uploadId = queuedFile?.upload_id || uuidv4();
    const { setFiles } = FileStore.getState();
    const file__path = queuedFile?.file__path || `${Date.now()}_${file.name}`;
    const nFile = {
      name: file.name,
      size: file.size,
      downloadURL: "",
      file__path: file__path,
      progress: 0,
      upload_id: uploadId,
    };

    setFiles([...(FileStore.getState().files || []), nFile]);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("chat_id", chat_id);
    formData.append("upload_id", uploadId);
    if (space?.space_id) {
      formData.append("space_id", space.space_id);
    }

    return api({
      method: "POST",
      url: "/storage/assets/upload",
      data: formData,
      onUploadProgress: (e) => {
        const currentProgress = e.total ? Math.round((e.loaded / e.total) * 100) : 0;
        setFiles(
          (FileStore.getState().files || []).map((f) =>
            f?.file__path === file__path
              ? { ...f, progress: mapValue(currentProgress, 0, 100, 0, 90) }
              : f
          )
        );
      },
    })
      .then(async (importResponse) => {
        const asset = importResponse?.data?.asset;
        if (!asset?.asset_id) throw new Error("Asset upload returned no asset_id");
        const ownerId = space?.owner_id ? space?.owner_id : user?.uid;
        const session_id = useSessionIdStore.getState().session_id;

        await api({
          method: "POST",
          url: ENDPOINTS.ADD_FILE_TO_SPARK,
          data: {
            chat_id,
            org_id: orgId,
            owner_id: ownerId,
            asset_id: asset.asset_id,
            file_toc: importResponse?.data?.file_toc ?? [],
            space_id: space?.space_id ?? null,
            by: user?.uid,
            session_id,
          },
        });

        const resolvedAsset = await mediaUrlAdapter.resolveAsset(asset.asset_id);

        setFiles(
          (FileStore.getState().files || []).map((f) =>
            f?.file__path === file__path
              ? {
                  ...f,
                  progress: 100,
                  downloadURL: resolvedAsset.url,
                  asset_id: asset.asset_id,
                  mime_type: asset.mime_type,
                }
              : f
          )
        );
        return asset.asset_id;
      })
      .catch((error) => {
        logger.error('fileUpload', 'Upload failed', { error: error.message, chat_id });
        throw error;
      });
  });

  const results = await Promise.allSettled(uploadPromises);
  const failedUpload = results.find((result) => result.status === "rejected");
  if (failedUpload) throw failedUpload.reason;
  return results.map((result) => result.value);
};

export const uploadFiles = async (chat_id, user, orgId, space) => {
  const { new_chat_files } = FileStore.getState();
  const queuedFiles = [...(new_chat_files || [])];
  FileStore.getState().setNewFiles([]);
  try {
    return await uploadMultipleFiles(
      queuedFiles,
      user,
      orgId,
      chat_id,
      space
    );
  } catch (error) {
    const queuedUploadIds = new Set(
      queuedFiles.map((file) => file?.upload_id).filter(Boolean)
    );
    FileStore.getState().setFiles((files) =>
      files.filter((file) => !queuedUploadIds.has(file?.upload_id))
    );
    FileStore.getState().setNewFiles(queuedFiles);
    logger.error('fileUpload', 'Error uploading files', { error: error.message });
    throw error;
  }
};
